import { useEffect, useRef, useState } from "react";
import { Captions, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { recaptionDocument, fetchDocument } from "../../lib/api";
import { Button } from "../ui/button";

// One-tap captions for letters recorded before timed transcripts existed. Polls the document until segments arrive.
export const AddCaptions = ({ doc, onUpdated }) => {
  const [busy, setBusy] = useState(Boolean(doc?.captioning));
  const timer = useRef(null);
  useEffect(() => { setBusy(Boolean(doc?.captioning)); }, [doc?.id, doc?.captioning]);
  useEffect(() => {
    if (!busy) return;
    timer.current = setInterval(async () => {
      try {
        const d = await fetchDocument(doc.id);
        if (!d.captioning) {
          clearInterval(timer.current); setBusy(false); onUpdated?.(d);
          if (d.transcript_segments?.length) toast.success("Captions added", { description: `${d.transcript_segments.length} lines, synced to the recording.` });
          else toast.error(d.caption_error || "Couldn't add captions");
        }
      } catch { /* keep polling */ }
    }, 4000);
    return () => clearInterval(timer.current);
  }, [busy, doc?.id, onUpdated]);
  const go = async () => {
    try { await recaptionDocument(doc.id); setBusy(true); toast("Listening closely…", { description: "Captions usually take under a minute." }); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't start captioning"); }
  };
  return (
    <div className="mt-2 flex flex-wrap items-center gap-2 rounded-xl border border-dashed border-primary/40 bg-primary/5 px-3 py-2" data-testid="add-captions">
      <Captions className="h-4 w-4 text-primary" />
      <span className="flex-1 text-sm">{busy ? "Adding timed captions…" : "This letter has no timed captions yet."}</span>
      <Button size="sm" variant="outline" className="rounded-full" onClick={go} disabled={busy} data-testid="add-captions-btn">
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Captions className="h-3.5 w-3.5" />} {busy ? "Working" : "Add captions"}
      </Button>
      {!busy && doc?.caption_error && <p className="w-full text-xs text-destructive" data-testid="add-captions-error">{doc.caption_error}</p>}
    </div>
  );
};
